import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Radio, Space, Table, Tag, Upload, message } from "antd";
import { DeleteOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";

export default function FastReplyPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8080";
  const token = useMemo(() => localStorage.getItem("token"), []);
  const typeValue = Form.useWatch("type", form);
  const contentValue = Form.useWatch("content", form);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewType, setPreviewType] = useState("image");
  const [previewSrc, setPreviewSrc] = useState("");

  const loadList = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const resp = await fetch(`${baseUrl}/manager/line/list`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.error(msg || "加载失败");
        return;
      }
      setList(result?.data?.list || []);
    } catch (error) {
      message.error("加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: "text", nickname: "" });
    setFileList([]);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      type: record.type || "text",
      content: record.content,
      nickname: record.nickname || "",
    });
    setFileList([]);
    setModalOpen(true);
  };

  const uploadToOss = async (file, dir) => {
    const params = new URLSearchParams({ dir, filename: file?.name || "" });
    const resp = await fetch(`${baseUrl}/aliyun/oss/upload?${params}`, {
      headers: token ? { token } : {},
    });
    const result = await resp.json();
    if (result?.code) {
      throw new Error(result?.msg || "获取上传策略失败");
    }
    const upload = result?.data?.payload;
    if (!upload?.host || !upload?.key || !upload?.policy || !upload?.signature || !upload?.accessId) {
      throw new Error("上传策略不完整");
    }
    const formData = new FormData();
    formData.append("key", upload.key);
    formData.append("policy", upload.policy);
    formData.append("OSSAccessKeyId", upload.accessId);
    formData.append("signature", upload.signature);
    formData.append("success_action_status", "200");
    formData.append("file", file);
    const uploadResp = await fetch(upload.host, {
      method: "POST",
      body: formData,
    });
    if (!uploadResp.ok) {
      throw new Error("上传失败");
    }
    return upload.key;
  };

  const getAccessUrl = (key, imageResize) => {
    const name = String(key || "").split("/").pop() || "file";
    const params = new URLSearchParams({ key, filename: name, mode: "inline" });
    if (token) params.set("token", token);
    if (imageResize) params.set("imageResize", imageResize);
    return `${baseUrl}/aliyun/oss/access?${params}`;
  };

  useEffect(() => {
    if (!modalOpen) return;
    const type = String(typeValue || "text");
    const key = String(contentValue || "").trim();
    if (type !== "image" && type !== "video") {
      setFileList([]);
      return;
    }
    if (!key) {
      setFileList([]);
      return;
    }
    let canceled = false;
    const filename = key.split("/").pop() || key;
    const url = getAccessUrl(key, type === "video" ? "video/snapshot,t_0,f_jpg" : undefined);
    if (!canceled) {
      setFileList([{ uid: key, name: filename, status: "done", url }]);
    }
    return () => {
      canceled = true;
    };
  }, [modalOpen, typeValue, contentValue]);

  const handleCustomUpload = async ({ file, onSuccess, onError }) => {
    const type = String(form.getFieldValue("type") || "text");
    if (type !== "image" && type !== "video") {
      onError?.(new Error("类型不合法"));
      return;
    }
    const dir = "fast-reply";
    setUploading(true);
    try {
      const key = await uploadToOss(file, dir);
      // 先占位，useEffect 监听 contentValue 变化后会自动调 download 接口填充 url
      setFileList([
        {
          uid: key,
          name: key.split("/").pop() || file?.name || "file",
          status: "uploading",
          percent: 99,
          url: "",
        },
      ]);
      form.setFieldsValue({ content: key });
      message.success("上传成功");
      onSuccess?.({ ok: true }, file);
    } catch (error) {
      message.error(String(error?.message || error) || "上传失败");
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = (file) => {
    const type = String(form.getFieldValue("type") || "text");
    const key = String(form.getFieldValue("content") || "").trim();
    const filename = file?.name || (key ? key.split("/").pop() : "") || "file";
    let url = file?.url || "";
    if (!url && key) {
      url = getAccessUrl(key);
    }
    setPreviewTitle(filename);
    setPreviewType(type);
    setPreviewSrc(url);
    setPreviewOpen(true);
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const params = new URLSearchParams({
        type: values.type,
        content: values.content,
        nickname: values.nickname || "",
      });
      if (editing?.id) {
        params.set("id", editing.id);
      }
      const url = editing?.id ? "/manager/line/edit" : "/manager/line/add";
      const resp = await fetch(`${baseUrl}${url}?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.error(msg || "保存失败");
        return;
      }
      message.success("保存成功");
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      loadList();
    } catch (error) {
      if (!error?.errorFields) {
        message.error("保存失败");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const params = new URLSearchParams({ id: record.id });
      const resp = await fetch(`${baseUrl}/manager/line/delete?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.error(msg || "删除失败");
        return;
      }
      message.success("删除成功");
      loadList();
    } catch (error) {
      message.error("删除失败");
    }
  };

  const handleClear = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const resp = await fetch(`${baseUrl}/manager/line/clear`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.error(msg || "清空失败");
        return;
      }
      message.success("清空成功");
      setList([]);
    } catch (error) {
      message.error("清空失败");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "序号",
      key: "index",
      width: 80,
      align: "center",
      render: (_value, _record, index) => index + 1,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 100,
      align: "center",
      render: (value) => {
        const t = String(value || "text");
        if (t === "image") return <Tag color="blue">图片</Tag>;
        if (t === "video") return <Tag color="purple">视频</Tag>;
        return <Tag color="green">文字</Tag>;
      },
    },
    {
      title: "昵称",
      dataIndex: "nickname",
      key: "nickname",
      width: 140,
      align: "center",
      render: (value, record) => {
        const t = String(record?.type || "text");
        if (t === "image") return String(value || "图片");
        if (t === "video") return String(value || "视频");
        return "-";
      },
    },
    {
      title: "话术内容",
      dataIndex: "content",
      key: "content",
      align: "center",
      render: (value, record) => {
        const t = String(record?.type || "text");
        const raw = String(value || "");
        if (t === "image") {
          return (
            <img
              src={getAccessUrl(raw, "image/resize,m_lfit,w_480,h_480")}
              alt=""
              style={{ maxHeight: 60, maxWidth: 120, objectFit: "contain", borderRadius: 4, display: "block", margin: "0 auto", cursor: "pointer" }}
              onClick={() => {
                setPreviewTitle(raw.split("/").pop() || "图片");
                setPreviewType("image");
                setPreviewSrc(getAccessUrl(raw, "image/resize,m_lfit,w_1080,h_1080"));
                setPreviewOpen(true);
              }}
            />
          );
        }
        if (t === "video") {
          return (
            <img
              src={getAccessUrl(raw, "video/snapshot,t_0,f_jpg")}
              alt=""
              style={{ maxHeight: 60, maxWidth: 120, objectFit: "contain", borderRadius: 4, display: "block", margin: "0 auto", cursor: "pointer" }}
              onClick={() => {
                setPreviewTitle(raw.split("/").pop() || "视频");
                setPreviewType("video");
                setPreviewSrc(getAccessUrl(raw));
                setPreviewOpen(true);
              }}
            />
          );
        }
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ position: "relative", display: "inline-block", maxWidth: 320 }}>
              <div style={{
                background: "#dcf8c6",
                borderRadius: 8,
                padding: "6px 12px",
                wordBreak: "break-all",
                fontSize: 14,
              }}>
                {raw}
              </div>
              <div style={{
                position: "absolute",
                top: "50%",
                right: -8,
                transform: "translateY(-50%)",
                width: 0,
                height: 0,
                borderTop: "8px solid transparent",
                borderBottom: "8px solid transparent",
                borderLeft: "8px solid #dcf8c6",
              }} />
            </div>
          </div>
        );
      },
    },
    {
      title: "操作",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除该话术？" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Space>
          <Button type="primary" onClick={openAdd}>
            +新增话术
          </Button>
          <Button onClick={loadList} loading={loading}>
            刷新
          </Button>
        </Space>
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setClearModalOpen(true)}>
          清空
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        bordered
        pagination={{ pageSize: 10 }}
      />
      <Modal
        title={editing ? "编辑话术" : "新增话术"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            取消
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>
            保存
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="话术类型" rules={[{ required: true }]} style={{ marginBottom: 6 }}>
            <Radio.Group
              options={[
                { label: "文字", value: "text" },
                { label: "图片", value: "image" },
                { label: "视频", value: "video" },
              ]}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.type !== next.type}>
            {({ getFieldValue }) => {
              const type = getFieldValue("type") || "text";
              if (type === "text") {
                return (
                  <Form.Item name="content" label="话术内容" rules={[{ required: true }]}>
                    <Input.TextArea rows={4} placeholder="请输入话术内容" />
                  </Form.Item>
                );
              }
              return (
                <>
                  <Form.Item name="nickname" style={{ marginBottom: 6 }}>
                    <Input placeholder="自定义昵称" />
                  </Form.Item>
                  <Form.Item name="content" rules={[{ required: true }]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item label="上传文件" required style={{ marginTop: 10 }}>
                    <Upload
                      accept={type === "video" ? "video/*" : "image/*"}
                      listType="picture-card"
                      maxCount={1}
                      fileList={fileList}
                      customRequest={handleCustomUpload}
                      onChange={({ fileList: next }) => {
                        // 只保留删除操作（文件数减少），上传过程中的状态变更由 handleCustomUpload 管理
                        if (next.length < fileList.length) setFileList(next);
                      }}
                      onPreview={handlePreview}
                      onRemove={() => {
                        form.setFieldsValue({ content: "" });
                        setFileList([]);
                        return true;
                      }}
                      disabled={uploading}
                    >
                      {fileList.length >= 1 ? null : (
                        <div>
                          {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                          <div style={{ marginTop: 8 }}>{uploading ? "上传中" : "上传"}</div>
                        </div>
                      )}
                    </Upload>
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)} centered>
        {previewSrc ? (
          previewType === "video" ? (
            <video src={previewSrc} controls style={{ width: "100%" }} />
          ) : (
            <img alt={previewTitle} style={{ width: "100%" }} src={previewSrc} />
          )
        ) : null}
      </Modal>
      <Modal
        title="确认清空"
        open={clearModalOpen}
        onCancel={() => setClearModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setClearModalOpen(false)}>
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading}
            onClick={async () => {
              await handleClear();
              setClearModalOpen(false);
            }}
          >
            清空
          </Button>,
        ]}
      >
        确认清空所有话术？
      </Modal>
    </div>
  );
}
